import { ChangeEvent } from 'react';
import { FormHelperText, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';

import { CoreBox, CoreFormControl } from '@youscience/core';

import {
  GpaWeighting,
  RegGpaScale,
  FinalReportSchema,
  FinalReportRequest,
  YesNo,
} from '@youscience/commonapp-service-common';

import RadioButtonGroupWithClearButton from '@components/RadioGroupWithClearButton';
import MonthYearDatePicker from '@components/MonthYearDatePicker';
import SectionAccordion from '@components/SectionAccordion';

import { FormState } from '@constants/commonApp/types';

import { TabSections } from '@interfaces/recommenderWorkspace';

import ContinueButton from '@components/ContinueButton';
import { dateRangeValidation, handleZodValidation } from '../../../../common/formUtils';
import { StyledFieldsContainer } from '@pages/RecommenderSpace/components/Card.styles';
import { REQUIRED_FIELD_MESSAGE } from '@constants/texts';
import { maxDate, minDate } from '@pages/RecommenderSpace/components/constants';
import { useApplicantFormSection } from '@pages/StudentDetailsPage/hooks/useApplicantFormSection';

export type GPAFields = Partial<FinalReportRequest[TabSections.GPA]>;

interface GPAProps {
  gpa?: GPAFields;
  onFormChange: (formState: FormState<GPAFields>) => void;
  onExpand: () => void;
  onContinue?: () => void;
  isExtended: boolean;
  isSaved: boolean;
  showError?: boolean;
}

const additionalValidations = (newFormState: FormState<GPAFields>) => {
  return dateRangeValidation(newFormState, {});
};

const validator = FinalReportSchema.shape.gpa;

export const GPA = ({ gpa = {}, onFormChange, onExpand, onContinue, isExtended, isSaved, showError }: GPAProps) => {
  const [formState, setFormStateWithErrors] = useApplicantFormSection<GPAFields, typeof validator>(
    gpa,
    validator,
    isSaved,
    onFormChange,
    additionalValidations,
  );

  const handleDateChange = (fieldName: string) => (value: Date | null) => {
    if (value) {
      setFormStateWithErrors({ [fieldName]: `${value.getMonth() + 1}/${value.getFullYear()}`.padStart(7, '0') });
    } else {
      setFormStateWithErrors({ [fieldName]: undefined });
    }
  };

  const handleSelectChange = (field: string) => (event: SelectChangeEvent<unknown>) => {
    setFormStateWithErrors({ [field]: (event.target.value as string) || '' });
  };

  const handleTextFieldChange =
    (field: string, { optional = false } = {}) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.value && handleZodValidation(field, validator, { [field]: event.target.value }).isError) return;
      setFormStateWithErrors({ [field]: event.target.value || (optional ? null : undefined) });
    };

  const handleRadioChange = (field: string) => (event: ChangeEvent<HTMLInputElement>) => {
    setFormStateWithErrors({ [field]: event.target.value });
  };

  return (
    <SectionAccordion
      title="GPA"
      isExpanded={isExtended}
      onChange={onExpand}
      isChanged={formState.hasChanged}
      showCheckmark={formState.isSaved && !!formState.isFormValid}
      isError={showError || !formState.isFormValid}
    >
      <StyledFieldsContainer>
        <RadioButtonGroupWithClearButton
          title="Do you report GPA? (Grade Point Average)"
          name="reporting"
          value={formState.data.reporting ?? undefined}
          options={YesNo}
          required
          handleRadioChange={handleRadioChange}
          handleClear={() => setFormStateWithErrors({ reporting: undefined })}
          error={!!formState.errors.reporting}
          helperText={formState.errors.reporting}
        />

        {formState.data.reporting === '0' ? (
          <>
            <CoreBox sx={{ display: 'flex', gap: '1.5rem', alignItems: 'start' }}>
              <CoreFormControl fullWidth>
                <TextField
                  name="cumulative"
                  label="Cumulative GPA"
                  value={formState.data.cumulative ?? ''}
                  required
                  error={!!formState.errors.cumulative}
                  helperText={formState.errors.cumulative}
                  onChange={handleTextFieldChange('cumulative')}
                />
              </CoreFormControl>

              <CoreFormControl fullWidth error={!!formState.errors.scale}>
                <InputLabel required>GPA scale</InputLabel>

                <Select label="GPA scale" value={formState.data.scale ?? ''} onChange={handleSelectChange('scale')}>
                  {Object.entries(RegGpaScale).map(([key, value]) => {
                    return (
                      <MenuItem key={value} value={value}>
                        {key}
                      </MenuItem>
                    );
                  })}
                </Select>
                {formState.errors.scale && <FormHelperText>{formState.errors.scale}</FormHelperText>}
              </CoreFormControl>
            </CoreBox>

            <CoreBox sx={{ display: 'flex', gap: '1.5rem', alignItems: 'start' }}>
              <MonthYearDatePicker
                name="startDate"
                label="From (start date)"
                required
                slotProps={{
                  inputProps: { clearable: true, onClear: () => handleDateChange('startDate') },
                  error: !formState.data.startDate || Boolean(formState.errors['startDate']),
                }}
                value={formState.data.startDate ?? undefined}
                error={!formState.data.startDate ? REQUIRED_FIELD_MESSAGE : formState.errors.startDate}
                handleDateChange={handleDateChange('startDate')}
                minDate={minDate}
                maxDate={maxDate}
              />

              <MonthYearDatePicker
                name="endDate"
                label="Ending date (final date)"
                required
                slotProps={{
                  inputProps: { clearable: true, onClear: () => handleDateChange('endDate') },
                  error: !formState.data.endDate || Boolean(formState.errors['endDate']),
                }}
                value={formState.data.endDate ?? undefined}
                error={!formState.data.endDate ? REQUIRED_FIELD_MESSAGE : formState.errors.endDate}
                handleDateChange={handleDateChange('endDate')}
                minDate={minDate}
                maxDate={maxDate}
              />
            </CoreBox>

            <RadioButtonGroupWithClearButton
              title="GPA weighting"
              name="weighting"
              value={formState.data.weighting ?? undefined}
              options={GpaWeighting}
              required
              handleRadioChange={handleRadioChange}
              handleClear={() => setFormStateWithErrors({ weighting: undefined })}
              error={!!formState.errors.weighting}
              helperText={formState.errors.weighting}
            />

            <CoreBox sx={{ display: 'flex', gap: '1.5rem' }}>
              <CoreFormControl fullWidth>
                <TextField
                  name="passing"
                  label="School's passing mark"
                  value={formState.data.passing ?? ''}
                  required
                  error={!!formState.errors.passing}
                  helperText={formState.errors.passing}
                  onChange={handleTextFieldChange('passing')}
                />
              </CoreFormControl>

              <CoreFormControl fullWidth>
                <TextField
                  name="highest"
                  label="Highest grade/GPA in class"
                  value={formState.data.highest ?? ''}
                  required
                  error={!!formState.errors.highest}
                  helperText={formState.errors.highest}
                  onChange={handleTextFieldChange('highest', { optional: true })}
                />
              </CoreFormControl>
            </CoreBox>
          </>
        ) : null}

        <ContinueButton onContinue={onContinue} />
      </StyledFieldsContainer>
    </SectionAccordion>
  );
};
